/**
 * Normalized records the desktop stores locally (worker) and renders (renderer). These are the
 * shapes behind the IPC command outputs; the server never sees them.
 */
import { z } from 'zod';
import {
  AccountIdSchema,
  AgendaIdSchema,
  ArtifactIdSchema,
  CanonicalKeySchema,
  ConnectionIdSchema,
  DatasetIdSchema,
  DatasetRunIdSchema,
  IssueIdSchema,
  ObjectTypeSchema,
  OperationIdSchema,
  OutreachBatchIdSchema,
  OutreachItemIdSchema,
  OverrideIdSchema,
  ProviderSchema,
  RecordKeySchema,
  RumiSessionIdSchema,
  RumiTurnIdSchema,
  RunIdSchema,
  SnapshotIdSchema,
  TabIdSchema,
  TaskGroupIdSchema,
  TemplateIdSchema,
  UserIdSchema,
  WorkspaceIdSchema,
} from '../ids.js';
import {
  CompletenessStateSchema,
  DatasetRunStateSchema,
  EmployeeCheckSchema,
  EmployeeCheckStatusSchema,
  EmploymentStateSchema,
  RemedyActionSchema,
  SeverityLevelSchema,
  StandardStatusSchema,
  SyncErrorCodeSchema,
  SyncJobStateSchema,
  TriSchema,
} from '../status.js';
// notifications change: type-only (api/entities imports a value from this module; a value import here would be a cycle).
import type { MutableCategory } from '../api/entities.js';

const isoDateTime = z.iso.datetime();
const count = z.number().int().nonnegative();

/** Current normalized-record schema version. Bump when SourceRecord shape changes. */
export const SOURCE_RECORD_SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------------------------
// Source records and overrides
// ---------------------------------------------------------------------------------------------

export const LinkSchema = z.object({ label: z.string().min(1).max(200), url: z.string().url() });
export type Link = z.infer<typeof LinkSchema>;

export const ClassificationSchema = z.object({
  isPolicy: TriSchema,
  isInfrastructure: TriSchema,
  isEmployeeCompliance: TriSchema,
  isCompanyCompliance: TriSchema,
});
export type Classification = z.infer<typeof ClassificationSchema>;

export const ClassificationPatchSchema = z.object({
  isPolicy: TriSchema.optional(),
  isInfrastructure: TriSchema.optional(),
  isEmployeeCompliance: TriSchema.optional(),
  isCompanyCompliance: TriSchema.optional(),
  customAskNeeded: z.boolean().optional(),
});
export type ClassificationPatch = z.infer<typeof ClassificationPatchSchema>;

export const RecordTimestampsSchema = z.object({
  sourceUpdatedAt: isoDateTime.nullable().optional(),
  firstSeenAt: isoDateTime,
  collectedAt: isoDateTime,
  removedAt: isoDateTime.nullable().optional(),
});
export type RecordTimestamps = z.infer<typeof RecordTimestampsSchema>;

export const AgencyAiDescriptionSchema = z.object({
  sourceHash: z.string().regex(/^[a-f0-9]{64}$/),
  promptVersion: z.string(),
  model: z.string(),
  generatedAt: isoDateTime,
  omitFromAgenda: z.boolean(),
});
export type AgencyAiDescriptionMetadata = z.infer<typeof AgencyAiDescriptionSchema>;

export const SourceRecordSchema = z.object({
  recordKey: RecordKeySchema,
  canonicalKey: CanonicalKeySchema.nullable(),
  provider: ProviderSchema,
  accountId: AccountIdSchema,
  workspaceId: WorkspaceIdSchema.nullable().optional(),
  objectType: ObjectTypeSchema,
  sourceId: z.string().min(1),
  title: z.string(),
  description: z.string().nullable(),
  /** Derived locally after sync; the original description and raw provider payload stay intact. */
  agencyAiDescription: z.string().max(20_000).nullable().optional(),
  agencyAi: AgencyAiDescriptionSchema.nullable().optional(),
  rawStatus: z.string().nullable(),
  status: StandardStatusSchema,
  severity: SeverityLevelSchema.nullable().optional(),
  deactivated: z.boolean(),
  /** Provider / published-rule classification. Overrides are stored separately. */
  classification: ClassificationSchema,
  frameworkIds: z.array(z.string()),
  parentKey: RecordKeySchema.nullable().optional(),
  childCount: count,
  links: z.array(LinkSchema),
  timestamps: RecordTimestampsSchema,
  /** Provider-supplied remediation text (lowest precedence in ask resolution). */
  remediation: z.string().nullable().optional(),
  /** Raw provider payload as fetched (retained verbatim, encrypted at rest). */
  raw: z.unknown(),
  rawDigest: z.string().regex(/^[a-f0-9]{64}$/).optional(),
  schemaVersion: z.number().int().positive(),
});
export type SourceRecord = z.infer<typeof SourceRecordSchema>;

export const OVERRIDE_SCOPES = ['account', 'library'] as const;
export const OverrideScopeSchema = z.enum(OVERRIDE_SCOPES);
export type OverrideScope = z.infer<typeof OverrideScopeSchema>;

/** Analyst override; survives sync. Account scope keys by recordKey, library scope by canonicalKey. */
export const OverrideSchema = z.object({
  id: OverrideIdSchema,
  scope: OverrideScopeSchema,
  accountId: AccountIdSchema.nullable(),
  recordKey: RecordKeySchema.nullable(),
  canonicalKey: CanonicalKeySchema.nullable(),
  included: z.boolean().nullable().optional(),
  irrelevant: z.boolean().nullable().optional(),
  classification: ClassificationPatchSchema.nullable().optional(),
  remediationText: z.string().max(20_000).nullable().optional(),
  customAskNeeded: z.boolean().nullable().optional(),
  displayDescription: z.string().max(20_000).nullable().optional(),
  baseVersion: z.number().int().nonnegative(),
  updatedAt: isoDateTime,
  updatedByUserId: UserIdSchema.nullable().optional(),
});
export type Override = z.infer<typeof OverrideSchema>;

export const ASK_PROVENANCE = [
  'account_override',
  'library_override',
  'agency_ai',
  'published_rule',
  'provider_remediation',
  /** Agenda-only: the lead-in sentence above a Drata monitor's failing children (GET-61). */
  'monitor_lead_in',
  'missing',
] as const;
export const AskProvenanceSchema = z.enum(ASK_PROVENANCE);
export type AskProvenance = z.infer<typeof AskProvenanceSchema>;

export const EffectiveAskSchema = z.object({
  text: z.string().nullable(),
  provenance: AskProvenanceSchema,
  /** Rule/override that supplied the text, when applicable. */
  sourceKey: z.string().nullable().optional(),
  ruleVersion: z.number().int().nullable().optional(),
});
export type EffectiveAsk = z.infer<typeof EffectiveAskSchema>;

/** Published classification / remediation rule from the content bundle or the personal library. */
export const RuleSchema = z.object({
  canonicalKey: CanonicalKeySchema,
  provider: ProviderSchema,
  objectType: ObjectTypeSchema,
  catalogId: z.string().min(1),
  displayName: z.string(),
  displayDescription: z.string().nullable(),
  classification: ClassificationSchema,
  customAskNeeded: z.boolean(),
  remediationText: z.string().nullable(),
  source: z.enum(['published', 'library']),
  version: z.number().int().nonnegative(),
  contentVersion: z.string().nullable(),
  updatedAt: isoDateTime,
});
export type Rule = z.infer<typeof RuleSchema>;

// ---------------------------------------------------------------------------------------------
// Sync runs, datasets, issues
// ---------------------------------------------------------------------------------------------

export const SYNC_INITIATING_ACTIONS = ['sync_now', 'retry_failed', 'resume', 'connection_test'] as const;
export const SyncInitiatingActionSchema = z.enum(SYNC_INITIATING_ACTIONS);
export type SyncInitiatingAction = z.infer<typeof SyncInitiatingActionSchema>;

export const HttpMethodSchema = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
export type HttpMethod = z.infer<typeof HttpMethodSchema>;

/** Every field of spec section 6 "Error inspection". Scrubbed: no bodies, no raw paths. */
export const SyncIssueSchema = z.object({
  id: IssueIdSchema,
  runId: RunIdSchema,
  provider: ProviderSchema,
  connectionId: ConnectionIdSchema,
  accountId: AccountIdSchema,
  dataset: DatasetIdSchema.nullable(),
  endpointTemplate: z.string(),
  method: HttpMethodSchema,
  pageOrdinal: z.number().int().nonnegative().nullable(),
  httpStatus: z.number().int().min(100).max(599).nullable(),
  providerRequestId: z.string().nullable(),
  errorCode: SyncErrorCodeSchema,
  retryAttempt: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative().nullable(),
  timestamp: isoDateTime,
  nextRetryAt: isoDateTime.nullable(),
  suggestedRemedy: z.string(),
  remedyAction: RemedyActionSchema,
  /** Scrubbed one-line summary; never a response body. */
  summary: z.string().max(500),
  missingCapabilities: z.array(z.string()).optional(),
  resolved: z.boolean().default(false),
});
export type SyncIssue = z.infer<typeof SyncIssueSchema>;

export const DatasetRunSchema = z.object({
  id: DatasetRunIdSchema,
  runId: RunIdSchema,
  datasetId: DatasetIdSchema,
  state: DatasetRunStateSchema,
  startedAt: isoDateTime.nullable(),
  endedAt: isoDateTime.nullable(),
  pageCount: count,
  requestCount: count,
  retryCount: count,
  fetchedCount: count,
  /** Last durable checkpoint (cursor after the last committed page). */
  checkpointCursor: z.string().nullable(),
  lastPageOrdinal: z.number().int().nonnegative().nullable(),
  providerTotal: z.number().int().nonnegative().nullable(),
  errorCode: SyncErrorCodeSchema.nullable(),
  waitingUntil: isoDateTime.nullable(),
  unsupportedReason: z.string().nullable(),
  dependsOn: z.array(DatasetIdSchema).default([]),
  /** Reused from an earlier attempt when resuming (spec section 6). */
  resumedFromDatasetRunId: DatasetRunIdSchema.nullable().optional(),
});
export type DatasetRun = z.infer<typeof DatasetRunSchema>;

export const SyncTransitionSchema = z.object({
  from: SyncJobStateSchema.nullable(),
  to: SyncJobStateSchema,
  at: isoDateTime,
  reason: z.string().max(300).nullable(),
  byUserId: UserIdSchema.nullable(),
});
export type SyncTransition = z.infer<typeof SyncTransitionSchema>;

export const SyncSummarySchema = z.object({
  fetched: count,
  added: count,
  changed: count,
  removed: count,
  requests: count,
  retries: count,
  rateLimitWaits: count,
  retryWaitMs: count,
  permissionsSkipped: count,
  unsupportedSkipped: count,
  errors: count,
  elapsedMs: count,
});
export type SyncSummary = z.infer<typeof SyncSummarySchema>;

export const SyncRunSchema = z.object({
  id: RunIdSchema,
  accountId: AccountIdSchema,
  connectionId: ConnectionIdSchema,
  provider: ProviderSchema,
  workspaceId: WorkspaceIdSchema.nullable(),
  state: SyncJobStateSchema,
  initiatedByUserId: UserIdSchema,
  initiatingAction: SyncInitiatingActionSchema,
  operationId: OperationIdSchema,
  requestedDatasetIds: z.array(DatasetIdSchema),
  adapterVersion: z.string(),
  normalizationVersion: z.string(),
  queuedAt: isoDateTime,
  startedAt: isoDateTime.nullable(),
  endedAt: isoDateTime.nullable(),
  /** Set once committed (succeeded) or when a partial snapshot was retained. */
  snapshotId: SnapshotIdSchema.nullable(),
  previousSnapshotId: SnapshotIdSchema.nullable(),
  waitingUntil: isoDateTime.nullable(),
  /** Status text for the UI, e.g. "Waiting for Vanta · retry in 18s". */
  statusText: z.string(),
  transitions: z.array(SyncTransitionSchema),
  datasets: z.array(DatasetRunSchema),
  summary: SyncSummarySchema,
  issueCount: count,
  consistencyNote: z.string().nullable(),
  resumeOfRunId: RunIdSchema.nullable(),
  cancelRequested: z.boolean(),
  /** Set when the run needs a user Resume (paused, interrupted, crashed). */
  resumeRequired: z.boolean(),
  retryBudgetMs: count,
  retryBudgetUsedMs: count,
});
export type SyncRun = z.infer<typeof SyncRunSchema>;

// ---------------------------------------------------------------------------------------------
// Snapshots, coverage, diff
// ---------------------------------------------------------------------------------------------

export const DatasetCoverageSchema = z.object({
  id: DatasetIdSchema,
  state: CompletenessStateSchema,
  startedAt: isoDateTime.nullable(),
  endedAt: isoDateTime.nullable(),
  count: count,
  complete: z.boolean(),
  reason: z.string().nullable().optional(),
  /**
   * Structured outcome of the dataset run this entry was derived from (additive; absent on
   * manifests written before it existed). Consumers classify on this rather than on `reason`,
   * which is free provider text (`not_available`, `status_404`, ...).
   */
  runState: DatasetRunStateSchema.optional(),
});
export type DatasetCoverage = z.infer<typeof DatasetCoverageSchema>;

export const CoverageManifestSchema = z.object({
  snapshotId: SnapshotIdSchema.nullable(),
  accountId: AccountIdSchema,
  datasets: z.array(DatasetCoverageSchema),
  complete: z.boolean(),
  state: CompletenessStateSchema,
  generatedAt: isoDateTime,
  /** True when the provider lacks snapshot isolation; shown in history. */
  collectionWindowNote: z.string().nullable(),
});
export type CoverageManifest = z.infer<typeof CoverageManifestSchema>;

export const SnapshotSchema = z.object({
  id: SnapshotIdSchema,
  accountId: AccountIdSchema,
  connectionId: ConnectionIdSchema,
  runId: RunIdSchema,
  provider: ProviderSchema,
  workspaceId: WorkspaceIdSchema.nullable(),
  committedAt: isoDateTime,
  complete: z.boolean(),
  status: z.enum(['complete', 'partial']),
  stale: z.boolean(),
  isCurrent: z.boolean(),
  recordCount: count,
  datasetIds: z.array(DatasetIdSchema),
  coverage: CoverageManifestSchema,
  collectionStartedAt: isoDateTime,
  collectionEndedAt: isoDateTime,
  adapterVersion: z.string(),
  normalizationVersion: z.string(),
  schemaVersion: z.number().int().positive(),
  /** Set when retained beyond the 30-day window because it is the last complete snapshot. */
  retainedReason: z.enum(['in_window', 'last_complete', 'referenced']).nullable(),
  expiresAt: isoDateTime.nullable(),
});
export type Snapshot = z.infer<typeof SnapshotSchema>;

export const FieldChangeSchema = z.object({
  field: z.string(),
  before: z.unknown(),
  after: z.unknown(),
});
export type FieldChange = z.infer<typeof FieldChangeSchema>;

export const SnapshotDiffSchema = z.object({
  fromSnapshotId: SnapshotIdSchema,
  toSnapshotId: SnapshotIdSchema,
  added: z.array(RecordKeySchema),
  removed: z.array(RecordKeySchema),
  changed: z.array(z.object({ recordKey: RecordKeySchema, fieldChanges: z.array(FieldChangeSchema) })),
  summary: z.object({ added: count, removed: count, changed: count, unchanged: count }),
  /** Datasets present in only one of the two snapshots (comparison is partial for them). */
  datasetsNotComparable: z.array(DatasetIdSchema),
});
export type SnapshotDiff = z.infer<typeof SnapshotDiffSchema>;

// ---------------------------------------------------------------------------------------------
// Artifacts and provenance
// ---------------------------------------------------------------------------------------------

export const ARTIFACT_TYPES = [
  'agenda',
  'infrastructure_sheet',
  'employee_compliance_sheet',
  'vulnerability_sheet',
  'vulnerability_csv',
  'vulnerability_json',
  'sources_json',
  'support_bundle',
  'backup',
] as const;
export const ArtifactTypeSchema = z.enum(ARTIFACT_TYPES);
export type ArtifactType = z.infer<typeof ArtifactTypeSchema>;

export const PUBLICATION_STATES = [
  'local',
  'created',
  'created_with_warnings',
  'updated',
  'update_failed',
  'failed',
  'unlinked',
  'deleted_remote',
  'missing_remote',
] as const;
export const PublicationStateSchema = z.enum(PUBLICATION_STATES);
export type PublicationState = z.infer<typeof PublicationStateSchema>;

/** Every generated artifact records this (spec section 1: reproducible workflow results). */
export const GenerationProvenanceSchema = z.object({
  accountId: AccountIdSchema,
  snapshotId: SnapshotIdSchema.nullable(),
  snapshotAt: isoDateTime.nullable(),
  connectionIds: z.array(ConnectionIdSchema),
  templateId: TemplateIdSchema.nullable(),
  templateVersion: z.number().int().nullable(),
  contentVersion: z.string().nullable(),
  filters: z.record(z.string(), z.unknown()),
  effectiveRulesDigest: z.string(),
  appVersion: z.string(),
  generatedAt: isoDateTime,
  generatedByUserId: UserIdSchema,
  coverage: CompletenessStateSchema,
  coverageNotes: z.array(z.string()),
  partialDataAcknowledged: z.boolean(),
});
export type GenerationProvenance = z.infer<typeof GenerationProvenanceSchema>;

export const STAGE_NAMES = ['create', 'content', 'formatting', 'links', 'validation', 'named_ranges', 'tabs'] as const;
export const StageResultSchema = z.object({
  stage: z.enum(STAGE_NAMES),
  status: z.enum(['succeeded', 'failed', 'skipped', 'warning']),
  message: z.string().nullable(),
  durationMs: count.nullable(),
});
export type StageResult = z.infer<typeof StageResultSchema>;

export const ArtifactSchema = z.object({
  id: ArtifactIdSchema,
  accountId: AccountIdSchema,
  type: ArtifactTypeSchema,
  title: z.string(),
  externalId: z.string().nullable(),
  url: z.string().url().nullable(),
  localPath: z.string().nullable(),
  publicationState: PublicationStateSchema,
  outputDigest: z.string(),
  provenance: GenerationProvenanceSchema,
  stageResults: z.array(StageResultSchema),
  warnings: z.array(z.string()),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  lastPublishedAt: isoDateTime.nullable(),
  agendaId: AgendaIdSchema.nullable().optional(),
});
export type Artifact = z.infer<typeof ArtifactSchema>;

// ---------------------------------------------------------------------------------------------
// Agendas
// ---------------------------------------------------------------------------------------------

export const AGENDA_SECTION_IDS = [
  'advisories',
  'agenda_items',
  'current_blockers',
  'customer_asks',
  'framework_status',
  'relevant_links',
  /** "Completed in Last Sprint:" (template feedback 2026-09-25; operator-owned). */
  'completed_last_sprint',
  /** Sprint history; lives on the "Previously completed sprints" tab in the default template. */
  'previously_completed',
  /** Managed overflow of capped ask groups; only present when a group exceeds its cap (Additional asks tab). */
  'additional_asks',
] as const;
export const AgendaSectionIdSchema = z.enum(AGENDA_SECTION_IDS);
export type AgendaSectionId = z.infer<typeof AgendaSectionIdSchema>;

/** Google Docs tabs an agenda document is made of (template feedback 2026-09-25). */
export const AGENDA_TABS = ['agenda', 'history', 'overflow'] as const;
export const AgendaTabSchema = z.enum(AGENDA_TABS);
export type AgendaTab = z.infer<typeof AgendaTabSchema>;

export const ASK_GROUPS = ['overall', 'infrastructure', 'company_compliance', 'employee_compliance'] as const;
export const AskGroupSchema = z.enum(ASK_GROUPS);
export type AskGroup = z.infer<typeof AskGroupSchema>;

export const AgendaSettingsSchema = z.object({
  includeOk: z.boolean(),
  includeAll: z.boolean(),
  /** Exclude policy-type records from customer asks (added by desktop-worker fix pass, R6). */
  disablePolicies: z.boolean().optional(),
  includeInfrastructureInAgenda: z.boolean(),
  /** Children listed per ask; null lists every child (template feedback 2026-09-25: mention everything). */
  childLimit: z.number().int().min(1).max(100).nullable(),
  /** Overall asks kept on the agenda tab; the rest go to the Additional asks tab. Absent: template default; null: no cap. */
  overallLimit: z.number().int().min(1).max(500).nullable().optional(),
  timezone: z.string().min(1),
});
export type AgendaSettings = z.infer<typeof AgendaSettingsSchema>;

export const AgendaSchema = z.object({
  id: AgendaIdSchema,
  accountId: AccountIdSchema,
  title: z.string(),
  meetingSeries: z.string().nullable(),
  documentId: z.string().nullable(),
  url: z.string().url().nullable(),
  isDefault: z.boolean(),
  templateId: TemplateIdSchema,
  templateVersion: z.number().int(),
  settings: AgendaSettingsSchema,
  markerMode: z.enum(['named_ranges', 'legacy_markers', 'unknown']),
  lastPublishedAt: isoDateTime.nullable(),
  lastSnapshotId: SnapshotIdSchema.nullable(),
  lastRevisionId: z.string().nullable(),
  publicationState: PublicationStateSchema,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  unlinkedAt: isoDateTime.nullable(),
});
export type Agenda = z.infer<typeof AgendaSchema>;

/** One rendered ask line inside an agenda preview. */
export const AgendaAskSchema = z.object({
  recordKey: RecordKeySchema,
  canonicalKey: CanonicalKeySchema.nullable(),
  title: z.string(),
  text: z.string(),
  group: AskGroupSchema,
  provenance: AskProvenanceSchema,
  status: StandardStatusSchema,
  children: z.array(z.object({ label: z.string(), multiplicity: z.number().int().positive() })),
  childCount: count,
  omittedChildCount: count,
  links: z.array(LinkSchema),
});
export type AgendaAsk = z.infer<typeof AgendaAskSchema>;

export const FrameworkStatusRowSchema = z.object({
  frameworkId: z.string(),
  name: z.string(),
  metrics: z.array(
    z.object({
      family: z.enum(['controls', 'tests', 'documents', 'monitors']),
      numerator: z.number().int().nonnegative().nullable(),
      denominator: z.number().int().nonnegative().nullable(),
      /** "Unknown" when the denominator is missing (spec section 8). */
      label: z.string(),
    }),
  ),
});
export type FrameworkStatusRow = z.infer<typeof FrameworkStatusRowSchema>;

export const AgendaSectionSchema = z.object({
  id: AgendaSectionIdSchema,
  heading: z.string(),
  managed: z.boolean(),
  /** Restricted Markdown for managed sections; operator-owned sections are passed through. */
  markdown: z.string(),
  /** Tab the section is written to. */
  tab: AgendaTabSchema.default('agenda'),
  /** Operator-owned line under the heading, written on create only. */
  intro: z.string().nullable().default(null),
});
export type AgendaSection = z.infer<typeof AgendaSectionSchema>;

export const AgendaOverflowSchema = z.object({ group: AskGroupSchema, shown: count, total: count });
export type AgendaOverflow = z.infer<typeof AgendaOverflowSchema>;

export const AgendaPreviewSchema = z.object({
  agendaId: AgendaIdSchema.nullable(),
  /** Google Docs file title. */
  documentTitle: z.string().default(''),
  /** Bold header lines above the meeting line (empty for the legacy single-title layout). */
  headerLines: z.array(z.string()).default([]),
  /** Bold meeting line (`Monday, August 10th, 2026 | Asynchronous Update`); null renders the legacy "Meeting date:" line. */
  meetingLine: z.string().nullable().default(null),
  tabTitles: z.object({ agenda: z.string(), history: z.string(), overflow: z.string() }).default({ agenda: 'Agenda', history: 'Previously completed sprints', overflow: 'Additional asks' }),
  sections: z.array(AgendaSectionSchema),
  asksByGroup: z.record(AskGroupSchema, z.array(AgendaAskSchema)),
  /** Groups whose asks exceed their cap; `shown` stay on the agenda tab, the rest go to the Additional asks tab. */
  overflow: z.array(AgendaOverflowSchema).default([]),
  /** Asks published with their fallback text because no ask text exists. */
  missingAskCount: count.default(0),
  frameworkStatus: z.array(FrameworkStatusRowSchema),
  warnings: z.array(z.string()),
  provenance: GenerationProvenanceSchema,
  meetingDate: z.string().nullable(),
});
export type AgendaPreview = z.infer<typeof AgendaPreviewSchema>;

export const SemanticDiffSchema = z.object({
  sections: z.array(
    z.object({
      id: AgendaSectionIdSchema,
      change: z.enum(['unchanged', 'added', 'removed', 'modified']),
      hunks: z.array(z.object({ kind: z.enum(['equal', 'insert', 'delete']), text: z.string() })),
    }),
  ),
  asks: z.object({
    added: z.array(z.string()),
    removed: z.array(z.string()),
    changed: z.array(z.string()),
  }),
  /** Managed regions edited by hand since the last publish (spec section 8). */
  manualEditsInManagedRegions: z.array(z.object({ sectionId: AgendaSectionIdSchema, preview: z.string() })),
  remoteRevisionId: z.string().nullable(),
  basedOnRevisionId: z.string().nullable(),
});
export type SemanticDiff = z.infer<typeof SemanticDiffSchema>;

// ---------------------------------------------------------------------------------------------
// Report rows
// ---------------------------------------------------------------------------------------------

export const INFRASTRUCTURE_STATUS_CHOICES = ['Pending Response', 'Completed'] as const;

export const InfrastructureRowSchema = z.object({
  /** Hidden stable merge key (spec section 8). */
  sourceKey: z.string(),
  platform: z.string(),
  task: z.string(),
  children: z.array(z.object({ label: z.string(), multiplicity: z.number().int().positive() })),
  status: z.string().nullable(),
  notes: z.string().nullable(),
  /** Human-owned fields preserved from the previous sheet, when regenerating. */
  humanOwned: z.boolean(),
  retired: z.boolean(),
});
export type InfrastructureRow = z.infer<typeof InfrastructureRowSchema>;

export const EmployeeComplianceRowSchema = z.object({
  personKey: RecordKeySchema,
  name: z.string(),
  email: z.string(),
  employment: EmploymentStateSchema,
  checks: z.record(EmployeeCheckSchema, EmployeeCheckStatusSchema),
  failedChecks: z.array(EmployeeCheckSchema),
  sourceTimestamp: isoDateTime.nullable(),
  onboardingUrl: z.string().url().nullable(),
  excludedReason: z.string().nullable(),
});
export type EmployeeComplianceRow = z.infer<typeof EmployeeComplianceRowSchema>;

export const VulnerabilityDetailRowSchema = z.object({
  findingKey: RecordKeySchema,
  source: z.string(),
  asset: z.string(),
  assetId: z.string(),
  identifier: z.string(),
  packageName: z.string().nullable(),
  packageIdentifier: z.string().nullable(),
  fixedVersion: z.string().nullable(),
  severity: SeverityLevelSchema,
  dueDate: isoDateTime.nullable(),
  fixable: TriSchema,
  firstDetectedAt: isoDateTime.nullable(),
  groupKey: z.string(),
  /** Provider page for the finding (the sheet's Asset cell links here; template feedback 2026-09-25). */
  externalUrl: z.string().url().nullable().optional(),
});
export type VulnerabilityDetailRow = z.infer<typeof VulnerabilityDetailRowSchema>;

export const VulnerabilitySummaryRowSchema = z.object({
  groupKey: z.string(),
  packageName: z.string(),
  packageIdentifier: z.string().nullable(),
  representativeFindingKey: RecordKeySchema,
  worstSeverity: SeverityLevelSchema,
  earliestDueDate: isoDateTime.nullable(),
  fixedVersion: z.string().nullable(),
  affectedAssetCount: count,
  findingCount: count,
  fixable: TriSchema,
  detailFindingKeys: z.array(RecordKeySchema),
});
export type VulnerabilitySummaryRow = z.infer<typeof VulnerabilitySummaryRowSchema>;

export const VULNERABILITY_EMPTY_REASONS = [
  'none',
  'no_matching',
  'dataset_not_synced',
  'access_unavailable',
  'sync_incomplete',
] as const;
export const VulnerabilityEmptyReasonSchema = z.enum(VULNERABILITY_EMPTY_REASONS);
export type VulnerabilityEmptyReason = z.infer<typeof VulnerabilityEmptyReasonSchema>;

export const VulnerabilityFiltersSchema = z.object({
  includeNonFixable: z.boolean().default(false),
  includeUnknownFixability: z.boolean().default(false),
  severities: z.array(SeverityLevelSchema).optional(),
  search: z.string().max(200).optional(),
});
export type VulnerabilityFilters = z.infer<typeof VulnerabilityFiltersSchema>;

// ---------------------------------------------------------------------------------------------
// Outreach
// ---------------------------------------------------------------------------------------------

export const TASK_GROUP_CATEGORIES = [
  'policies_training',
  'mfa',
  'screenlock',
  'disk_encryption',
  'antivirus',
  'password_manager',
  'background_check',
  'device_monitoring',
  'custom',
  'offboarding',
] as const;
export const TaskGroupCategorySchema = z.enum(TASK_GROUP_CATEGORIES);
export type TaskGroupCategory = z.infer<typeof TaskGroupCategorySchema>;

export const OUTREACH_MODES = ['group_bcc', 'personalized'] as const;
export const OutreachModeSchema = z.enum(OUTREACH_MODES);
export type OutreachMode = z.infer<typeof OutreachModeSchema>;

export const RecipientSchema = z.object({
  personKey: RecordKeySchema,
  email: z.string(),
  displayName: z.string(),
  employment: EmploymentStateSchema,
  /** Failing entity / device keys that made this person eligible. */
  evidenceKeys: z.array(RecordKeySchema),
  /** The person's own onboarding page when the provider exposes one ({onboarding_url} in personalized mode). */
  onboardingUrl: z.string().nullable().optional(),
});
export type Recipient = z.infer<typeof RecipientSchema>;

export const QUARANTINE_REASONS = [
  'missing_email',
  'ambiguous_identity',
  'duplicate_identity',
  'former_staff',
  'future_hire_policy',
  'contractor_policy',
  'service_account',
  'out_of_scope',
  'no_matched_device',
  'deactivated_entity',
  // template feedback 2026-09-25: people being offboarded or already deactivated never receive mail.
  'offboarding',
  'deactivated_person',
] as const;
export const QuarantineReasonSchema = z.enum(QUARANTINE_REASONS);

export const QuarantinedRecipientSchema = z.object({
  personKey: RecordKeySchema.nullable(),
  email: z.string().nullable(),
  displayName: z.string(),
  reasons: z.array(QuarantineReasonSchema).min(1),
});
export type QuarantinedRecipient = z.infer<typeof QuarantinedRecipientSchema>;

export const TaskGroupSchema = z.object({
  id: TaskGroupIdSchema,
  category: TaskGroupCategorySchema,
  name: z.string(),
  provider: ProviderSchema,
  /** `unsupported`: people the provider exposes no data for on this group's checks; never shown as passing. */
  statusCounts: z.object({ failing: count, passing: count, deactivated: count, unknown: count, unsupported: count.default(0) }),
  taskDetails: z.array(z.object({ recordKey: RecordKeySchema, title: z.string(), status: StandardStatusSchema })),
  dueDates: z.array(isoDateTime),
  affectedPeople: z.array(RecipientSchema),
  quarantined: z.array(QuarantinedRecipientSchema),
  supported: z.boolean(),
  unsupportedReason: z.string().nullable(),
  /** Prior draft/send on the same task and recipient set, for the duplication warning. */
  lastBatchAt: isoDateTime.nullable(),
  lastBatchId: OutreachBatchIdSchema.nullable(),
});
export type TaskGroup = z.infer<typeof TaskGroupSchema>;

export const OUTREACH_ITEM_STATES = [
  'prepared',
  'draft_created',
  'send_requested',
  'sent',
  'failed',
  'outcome_unknown',
] as const;
export const OutreachItemStateSchema = z.enum(OUTREACH_ITEM_STATES);
export type OutreachItemState = z.infer<typeof OutreachItemStateSchema>;

export const OutreachItemSchema = z.object({
  id: OutreachItemIdSchema,
  batchId: OutreachBatchIdSchema,
  recipients: z.array(RecipientSchema).min(1),
  /** Customer point of contact copied on the message (template feedback 2026-09-25); never an employee. */
  cc: z.array(z.string()).default([]),
  subject: z.string(),
  bodyDigest: z.string(),
  state: OutreachItemStateSchema,
  draftId: z.string().nullable(),
  messageId: z.string().nullable(),
  threadId: z.string().nullable(),
  operationId: OperationIdSchema.nullable(),
  requestedAt: isoDateTime.nullable(),
  sentAt: isoDateTime.nullable(),
  error: z.string().nullable(),
  reconcileAttempts: count,
  handedOffToGmail: z.boolean(),
  // real-providers change: browser link to this draft (Gmail with authuser = the connected Google
  // email, or the sim viewer in local mode). Computed by the worker from the current identity.
  gmailUrl: z.string().nullable().optional(),
});
export type OutreachItem = z.infer<typeof OutreachItemSchema>;

export const OutreachBatchSchema = z.object({
  id: OutreachBatchIdSchema,
  accountId: AccountIdSchema,
  taskGroupId: TaskGroupIdSchema,
  taskGroupName: z.string(),
  mode: OutreachModeSchema,
  templateId: TemplateIdSchema,
  templateVersion: z.number().int(),
  snapshotId: SnapshotIdSchema,
  snapshotAt: isoDateTime,
  sender: z.string(),
  createdAt: isoDateTime,
  createdByUserId: UserIdSchema,
  items: z.array(OutreachItemSchema),
  stateCounts: z.record(OutreachItemStateSchema, count),
  staleReason: z.string().nullable(),
  snapshotAgeAcknowledged: z.boolean(),
  priorBatchId: OutreachBatchIdSchema.nullable(),
  employeeListUsed: z.boolean(),
  /** Recipient policy in force when the batch was assembled (added by desktop-worker fix pass, OR-11). */
  recipientPolicy: z.object({ includeContractors: z.boolean(), includeFutureHires: z.boolean() }).optional(),
  // real-providers change: drafts-only outreach. The batch-level Gmail Drafts link and the connected
  // Gmail identity the links are built for (null when Google is not connected).
  gmailDraftsUrl: z.string().nullable().optional(),
  gmailIdentity: z.string().nullable().optional(),
  /** Cc addresses and the analyst name the batch was rendered with (template feedback 2026-09-25). */
  cc: z.array(z.string()).optional(),
  analystName: z.string().nullable().optional(),
});
export type OutreachBatch = z.infer<typeof OutreachBatchSchema>;

export const RenderedMessageSchema = z.object({
  itemIndex: z.number().int().nonnegative(),
  to: z.array(z.string()),
  bcc: z.array(z.string()),
  /** Customer contacts copied on the message (never employees; template feedback 2026-09-25). */
  cc: z.array(z.string()).default([]),
  subject: z.string(),
  body: z.string(),
  recipients: z.array(RecipientSchema),
});
export type RenderedMessage = z.infer<typeof RenderedMessageSchema>;

// ---------------------------------------------------------------------------------------------
// Rumi
// ---------------------------------------------------------------------------------------------

export const RUMI_MODES = ['focused', 'full_review'] as const;
export const RumiModeSchema = z.enum(RUMI_MODES);
export type RumiMode = z.infer<typeof RumiModeSchema>;

export const RumiScopeSchema = z.object({
  snapshotId: SnapshotIdSchema.nullable(),
  datasetIds: z.array(DatasetIdSchema).optional(),
  objectTypes: z.array(ObjectTypeSchema).optional(),
  statuses: z.array(StandardStatusSchema).optional(),
  frameworkIds: z.array(z.string()).optional(),
  recordKeys: z.array(RecordKeySchema).optional(),
  includeNotes: z.boolean().default(false),
  includeOverrides: z.boolean().default(true),
});
export type RumiScope = z.infer<typeof RumiScopeSchema>;

export const RUMI_DATA_CATEGORIES = [
  'tests',
  'controls',
  'documents',
  'policies',
  'frameworks',
  'vulnerabilities',
  'people',
  'devices',
  'integrations',
  'vendors',
  'evidence',
  'notes',
  'overrides',
] as const;
export const RumiDataCategorySchema = z.enum(RUMI_DATA_CATEGORIES);

export const ContextManifestSchema = z.object({
  destination: z.object({ service: z.literal('openai'), baseUrl: z.string().url(), model: z.string(), store: z.literal(false) }),
  accountId: AccountIdSchema,
  accountName: z.string(),
  snapshotId: SnapshotIdSchema.nullable(),
  snapshotAt: isoDateTime.nullable(),
  mode: RumiModeSchema,
  scope: RumiScopeSchema,
  dataCategories: z.array(RumiDataCategorySchema),
  recordCount: count,
  estimatedInputTokens: count,
  estimatedOutputTokens: count,
  estimatedCostMicroUsd: count,
  budgetRemainingMicroUsd: count.nullable(),
  policyVersion: z.string(),
  batchCount: count,
  /** Digest the renderer must echo in rumi.ask (acknowledgement). */
  digest: z.string(),
  warnings: z.array(z.string()),
  generatedAt: isoDateTime,
});
export type ContextManifest = z.infer<typeof ContextManifestSchema>;

export const RumiCitationSchema = z.object({
  recordKey: RecordKeySchema,
  label: z.string(),
  snapshotId: SnapshotIdSchema,
  objectType: ObjectTypeSchema.nullable(),
  expired: z.boolean(),
});
export type RumiCitation = z.infer<typeof RumiCitationSchema>;

export const RumiCoverageSchema = z.object({
  processed: count,
  total: count,
  partial: z.boolean(),
  unprocessedReason: z.enum(['budget', 'context_size', 'provider_failure', 'cancelled', 'none']),
});
export type RumiCoverage = z.infer<typeof RumiCoverageSchema>;

export const USAGE_VERIFICATION_STATUSES = ['estimated', 'reconciled', 'unknown'] as const;
export const UsageVerificationStatusSchema = z.enum(USAGE_VERIFICATION_STATUSES);
export type UsageVerificationStatus = z.infer<typeof UsageVerificationStatusSchema>;

export const RumiUsageSchema = z.object({
  inputTokens: count,
  outputTokens: count,
  cachedTokens: count,
  reasoningTokens: count.nullable(),
  requestCount: count,
  estimatedCostMicroUsd: count,
  priceTableVersion: z.string(),
  verificationStatus: UsageVerificationStatusSchema,
  providerRequestIds: z.array(z.string()),
  /** True when a cancelled/failed stream may have incurred unreported charges. */
  unknownUsage: z.boolean(),
});
export type RumiUsage = z.infer<typeof RumiUsageSchema>;

export const RumiModelConfigSchema = z.object({
  model: z.string(),
  higherCapabilityModel: z.string().nullable(),
  maxOutputTokens: z.number().int().positive(),
  reasoningEffort: z.enum(['minimal', 'low', 'medium', 'high']).nullable(),
  temperature: z.number().nullable(),
  policyVersion: z.string(),
  contentVersion: z.string().nullable(),
});
export type RumiModelConfig = z.infer<typeof RumiModelConfigSchema>;

export const RUMI_TURN_STATUSES = ['queued', 'streaming', 'completed', 'partial', 'cancelled', 'failed'] as const;
export const RumiTurnStatusSchema = z.enum(RUMI_TURN_STATUSES);
export type RumiTurnStatus = z.infer<typeof RumiTurnStatusSchema>;

export const RumiToolCallSchema = z.object({
  tool: z.string(),
  summary: z.string(),
  durationMs: count.nullable(),
  resultCount: count.nullable(),
});

export const RumiTurnSchema = z.object({
  id: RumiTurnIdSchema,
  sessionId: RumiSessionIdSchema,
  accountId: AccountIdSchema,
  operationId: OperationIdSchema,
  question: z.string(),
  /** Sanitized Markdown (no raw HTML, no remote images). */
  answerMarkdown: z.string(),
  citations: z.array(RumiCitationSchema),
  coverage: RumiCoverageSchema,
  usage: RumiUsageSchema,
  modelConfig: RumiModelConfigSchema,
  status: RumiTurnStatusSchema,
  mode: RumiModeSchema,
  scope: RumiScopeSchema,
  snapshotId: SnapshotIdSchema.nullable(),
  contextManifestDigest: z.string(),
  toolCalls: z.array(RumiToolCallSchema),
  startedAt: isoDateTime,
  endedAt: isoDateTime.nullable(),
  error: z.string().nullable(),
});
export type RumiTurn = z.infer<typeof RumiTurnSchema>;

export const RumiSessionSchema = z.object({
  id: RumiSessionIdSchema,
  accountId: AccountIdSchema,
  /** Legacy sessions have no owner and are not exposed or replayed for any signed-in user. */
  userId: UserIdSchema.optional(),
  title: z.string(),
  snapshotId: SnapshotIdSchema.nullable(),
  defaultMode: RumiModeSchema,
  turnCount: count,
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
  /** Conversations expire 90 days after last update (spec section 7). */
  expiresAt: isoDateTime,
  lastUsage: RumiUsageSchema.nullable(),
});
export type RumiSession = z.infer<typeof RumiSessionSchema>;

// ---------------------------------------------------------------------------------------------
// Notes, schedule, layout, display prefs, settings
// ---------------------------------------------------------------------------------------------

export const NOTE_SCOPES = ['global', 'account'] as const;
export const NoteScopeSchema = z.enum(NOTE_SCOPES);
export type NoteScope = z.infer<typeof NoteScopeSchema>;

export const NoteSchema = z.object({
  scope: NoteScopeSchema,
  accountId: AccountIdSchema.nullable(),
  content: z.string(),
  revision: z.number().int().nonnegative(),
  savedAt: isoDateTime.nullable(),
});
export type Note = z.infer<typeof NoteSchema>;

export const SCHEDULE_LIST_IDS = ['priority', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as const;
export const ScheduleListIdSchema = z.enum(SCHEDULE_LIST_IDS);
export type ScheduleListId = z.infer<typeof ScheduleListIdSchema>;

export const ScheduleStateSchema = z.object({
  priority: z.array(AccountIdSchema),
  monday: z.array(AccountIdSchema),
  tuesday: z.array(AccountIdSchema),
  wednesday: z.array(AccountIdSchema),
  thursday: z.array(AccountIdSchema),
  friday: z.array(AccountIdSchema),
  /** Per-list done markers; persist until reset by the user. */
  done: z.record(ScheduleListIdSchema, z.array(AccountIdSchema)),
  lastSelectedAccountId: AccountIdSchema.nullable(),
  timezone: z.string(),
  updatedAt: isoDateTime,
});
export type ScheduleState = z.infer<typeof ScheduleStateSchema>;

export const ACCOUNT_VIEWS = [
  'overview',
  'sources',
  'agendas',
  'vulnerabilities',
  'infrastructure',
  'employee_compliance',
  'outreach',
  'rumi',
  'rumi_code',
  'sync_history',
] as const;
export const AccountViewSchema = z.enum(ACCOUNT_VIEWS);
export type AccountView = z.infer<typeof AccountViewSchema>;

// notifications change: 'notifications' is the global notification center view (rail after Documentation).
export const GLOBAL_VIEWS = ['templates', 'notepad', 'documentation', 'notifications', 'settings', 'all_accounts'] as const;
export const GlobalViewSchema = z.enum(GLOBAL_VIEWS);
export type GlobalView = z.infer<typeof GlobalViewSchema>;

export const OpenTabSchema = z.object({
  id: TabIdSchema,
  kind: z.enum(['account', 'global']),
  accountId: AccountIdSchema.nullable(),
  view: z.union([AccountViewSchema, GlobalViewSchema]),
  title: z.string(),
  pinned: z.boolean().default(false),
  params: z.record(z.string(), z.string()).optional(),
});
export type OpenTab = z.infer<typeof OpenTabSchema>;

export const LayoutStateSchema = z.object({
  railWidth: z.number().int().min(220).max(340),
  railCollapsed: z.boolean(),
  inspectorWidth: z.number().int().min(320).max(480),
  inspectorOpen: z.boolean(),
  bottomPanelHeight: z.number().int().min(120).max(600),
  bottomPanelOpen: z.boolean(),
  openTabs: z.array(OpenTabSchema),
  activeTabId: TabIdSchema.nullable(),
  /** Per-account persisted filters keyed by accountId then view. */
  perAccountFilters: z.record(z.string(), z.record(z.string(), z.unknown())),
  windowBounds: z
    .object({ x: z.number().int(), y: z.number().int(), width: z.number().int(), height: z.number().int() })
    .nullable(),
});
export type LayoutState = z.infer<typeof LayoutStateSchema>;

export const COLOR_PREFERENCES = ['gold', 'emerald', 'rust', 'slate', 'violet', 'none'] as const;

export const DisplayPrefsSchema = z.object({
  pinned: z.boolean(),
  shortLabel: z.string().max(24).nullable(),
  initials: z.string().max(3).nullable(),
  /** Retained from the prototype for a later approved treatment; not rendered as colour now. */
  colorPreference: z.enum(COLOR_PREFERENCES).nullable(),
  order: z.number().int().nullable(),
});
export type DisplayPrefs = z.infer<typeof DisplayPrefsSchema>;

/**
 * Customer points of contact for outreach (template feedback 2026-09-25): the addresses the
 * analyst may Cc on employee outreach when the customer asks to be included. Local to the
 * device; never an employee address.
 */
export const OutreachContactsSchema = z.object({
  emails: z.array(z.string().email().max(254)).max(10),
  /** Pre-select the contacts in the compose pane. */
  ccByDefault: z.boolean(),
});
export type OutreachContacts = z.infer<typeof OutreachContactsSchema>;
export const DEFAULT_OUTREACH_CONTACTS: OutreachContacts = { emails: [], ccByDefault: false };

// notifications change ---------------------------------------------------------------------------

export const NOTIFICATION_TOAST_MODES = ['all', 'warnings_and_above', 'none'] as const;
export const NotificationToastModeSchema = z.enum(NOTIFICATION_TOAST_MODES);
export type NotificationToastMode = z.infer<typeof NotificationToastModeSchema>;

/**
 * One switch per mutable category (api MUTABLE_CATEGORIES). `access`, `device`, `release` and
 * `audit` have no switch and `required` notices ignore these switches entirely. A muted category
 * is not recorded at all (no row, no toast, no native notice, no badge).
 */
export const NotificationCategoryPrefsSchema = z.object({
  sync: z.boolean().default(true),
  content: z.boolean().default(true),
  outreach: z.boolean().default(true),
  rumi: z.boolean().default(true),
  artifact: z.boolean().default(true),
  storage: z.boolean().default(true),
  system: z.boolean().default(true),
} satisfies Record<MutableCategory, z.ZodType>);
export type NotificationCategoryPrefs = z.infer<typeof NotificationCategoryPrefsSchema>;
export const DEFAULT_NOTIFICATION_CATEGORY_PREFS: Readonly<NotificationCategoryPrefs> = Object.freeze({
  sync: true,
  content: true,
  outreach: true,
  rumi: true,
  artifact: true,
  storage: true,
  system: true,
});

/**
 * Legacy switches (pre notifications change) and the category each one mirrors:
 * syncCompleted -> categories.sync, contentUpdated -> categories.content, storageWarnings -> categories.storage.
 * Writers set both sides of a pair (see withLegacyNotificationPrefs); readers combine them with
 * resolveNotificationCategoryPrefs so a settings blob written before this change (legacy false, no
 * `categories`) still mutes the category, and an old reader still sees a sensible boolean.
 */
export const LEGACY_NOTIFICATION_PREF_MAP = { syncCompleted: 'sync', contentUpdated: 'content', storageWarnings: 'storage' } as const satisfies Record<string, MutableCategory>;
export type LegacyNotificationPref = keyof typeof LEGACY_NOTIFICATION_PREF_MAP;

export const NotificationPreferencesSchema = z.object({
  categories: NotificationCategoryPrefsSchema.default(DEFAULT_NOTIFICATION_CATEGORY_PREFS),
  /** Native macOS notifications when the window is not focused (always for `required`). */
  native: z.boolean().default(true),
  /** Dock badge = unread count. */
  dockBadge: z.boolean().default(true),
  /** Which arriving notifications also toast. `required` notices always toast and stay until interacted with. */
  toasts: NotificationToastModeSchema.default('all'),
  /** Legacy mirror of categories.sync (see LEGACY_NOTIFICATION_PREF_MAP). */
  syncCompleted: z.boolean().default(true),
  /** Legacy mirror of categories.content. */
  contentUpdated: z.boolean().default(true),
  /** Legacy mirror of categories.storage. */
  storageWarnings: z.boolean().default(true),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferencesSchema>;

/** Effective category switches: a category is on only when its switch and its legacy mirror (if any) are both on. */
export function resolveNotificationCategoryPrefs(prefs: NotificationPreferences): NotificationCategoryPrefs {
  const resolved: NotificationCategoryPrefs = { ...prefs.categories };
  for (const [legacy, category] of Object.entries(LEGACY_NOTIFICATION_PREF_MAP) as [LegacyNotificationPref, MutableCategory][]) {
    resolved[category] = resolved[category] && prefs[legacy];
  }
  return resolved;
}

/** Patch for writers: the category switches plus their legacy mirrors so both stay in step. */
export function withLegacyNotificationPrefs(categories: NotificationCategoryPrefs): Pick<NotificationPreferences, 'categories' | LegacyNotificationPref> {
  return { categories, syncCompleted: categories.sync, contentUpdated: categories.content, storageWarnings: categories.storage };
}

/**
 * Interface size presets (Settings › Appearance; ⌘= / ⌘− / ⌘0 step through them). A preset is
 * applied as the window zoom factor, so text, controls and chrome scale together and keep the
 * proportions the layout was designed with.
 */
export const UI_SIZES = ['small', 'default', 'large', 'larger'] as const;
export const UiSizeSchema = z.enum(UI_SIZES);
export type UiSize = z.infer<typeof UiSizeSchema>;
export const UI_SIZE_FACTORS: Readonly<Record<UiSize, number>> = { small: 0.9, default: 1, large: 1.1, larger: 1.25 };
export const UI_SIZE_LABELS: Readonly<Record<UiSize, string>> = { small: 'Small', default: 'Default', large: 'Large', larger: 'Larger' };

/** The neighbouring size preset in `direction`; the same preset at either end of the scale. */
export function stepUiSize(current: UiSize, direction: 1 | -1): UiSize {
  const index = UI_SIZES.indexOf(current);
  const from = index < 0 ? UI_SIZES.indexOf('default') : index;
  return UI_SIZES[Math.min(UI_SIZES.length - 1, Math.max(0, from + direction))] ?? 'default';
}

/**
 * Spacing density presets (Settings › Appearance). Density scales the spacing unit behind every
 * padding, gap and row height (`html[data-density]` in tokens.css) and leaves text size alone.
 */
export const UI_DENSITIES = ['compact', 'default', 'comfortable'] as const;
export const UiDensitySchema = z.enum(UI_DENSITIES);
export type UiDensity = z.infer<typeof UiDensitySchema>;
export const UI_DENSITY_LABELS: Readonly<Record<UiDensity, string>> = { compact: 'Compact', default: 'Default', comfortable: 'Comfortable' };

export const DesktopSettingsSchema = z.object({
  displayTimezone: z.string(),
  storageBudgetBytes: z.number().int().positive(),
  storageWarnRatio: z.number().min(0.5).max(0.99),
  defaultOutreachMode: OutreachModeSchema,
  includeUnknownFixabilityByDefault: z.record(ProviderSchema, z.boolean()),
  autoDownloadContent: z.boolean(),
  /** appearance change: interface size and spacing density for this device (Settings › Appearance). */
  uiSize: UiSizeSchema,
  uiDensity: UiDensitySchema,
  // notifications change: categories/native/dockBadge/toasts added; the three legacy booleans stay readable.
  notifications: NotificationPreferencesSchema,
  agendaDefaults: AgendaSettingsSchema,
  outreachFreshnessHours: z.number().int().positive(),
  /** Read-only in local mode; shown in Settings. */
  serverBaseUrl: z.string().url(),
  mode: z.enum(['local', 'cloud']),
});
export type DesktopSettings = z.infer<typeof DesktopSettingsSchema>;
